import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Button from "../ui/Button";
import { ThemeToggle } from "../ui/ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/authFetch";
import FeedbackButton from "../feedback/FeedbackButton";

export type SellerNotification = {
  id: string;
  title: string;
  body: string;
  receivedAt: string;
  read: boolean;
  data?: Record<string, unknown>;
};

type SellerTopbarProps = {
  collapsed: boolean;
  onToggleSidebar: () => void;
  notifications?: SellerNotification[];
  unreadNotificationsCount?: number;
  onMarkNotificationAsRead?: (notificationId: string) => void;
  onClearNotifications?: () => void;
};

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    first_name?: string;
    last_name?: string;
    rol?: string;
  };
  error?: string;
};

const wrapperStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  padding: "14px 20px 0 20px",
};

const barStyle: React.CSSProperties = {
  minHeight: "60px",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  padding: "10px 14px",
};

const leftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
};

const titleWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: "19px",
  fontWeight: 800,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.15,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginTop: "2px",
};

const rightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
};

const profileChipStyle: React.CSSProperties = {
  minHeight: "38px",
  padding: "0 12px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "13px",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const compactButtonStyle: React.CSSProperties = {
  minHeight: "38px",
  minWidth: "38px",
  padding: "8px 10px",
  borderRadius: "14px",
};

const bellWrapStyle: React.CSSProperties = {
  position: "relative",
};

const bellButtonStyle: React.CSSProperties = {
  position: "relative",
  minHeight: "38px",
  minWidth: "38px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: "16px",
};

const badgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  minWidth: "18px",
  height: "18px",
  padding: "0 5px",
  borderRadius: "999px",
  background: "var(--primary)",
  color: "#FFFFFF",
  border: "2px solid var(--surface)",
  fontSize: "10px",
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
  position: "absolute",
  top: "52px",
  right: 0,
  width: "360px",
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "18px",
  border: "1px solid var(--border)",
  background: "var(--background)",
  color: "var(--text-primary)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
  overflow: "hidden",
  zIndex: 50,
};

const panelHeaderStyle: React.CSSProperties = {
  padding: "14px",
  borderBottom: "1px solid var(--border)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
};

const panelTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "14px",
  fontWeight: 800,
};

const panelActionStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  cursor: "pointer",
};

const listStyle: React.CSSProperties = {
  maxHeight: "360px",
  overflowY: "auto",
};

const itemStyle: React.CSSProperties = {
  padding: "14px",
  borderBottom: "1px solid var(--border)",
};

const itemUnreadStyle: React.CSSProperties = {
  background: "var(--primary-soft)",
  borderLeft: "3px solid var(--primary)",
};

const itemTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 800,
  marginBottom: "6px",
};

const itemBodyStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  lineHeight: 1.45,
};

const itemDateStyle: React.CSSProperties = {
  marginTop: "8px",
  fontSize: "11px",
  color: "var(--text-secondary)",
};

const itemActionStyle: React.CSSProperties = {
  marginTop: "10px",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-primary)",
  borderRadius: "999px",
  padding: "6px 10px",
  fontSize: "11px",
  fontWeight: 800,
  cursor: "pointer",
};

const emptyStyle: React.CSSProperties = {
  padding: "24px 14px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  textAlign: "center",
};

const getPageSubtitle = (pathname: string): string => {
  if (pathname === "/seller") return "Resumen operativo";
  if (pathname === "/seller/envios") return "Seguimiento de envíos";
  return "Área seller";
};

const buildFirstName = (firstName?: string, username?: string | null) => {
  const cleanFirstName = String(firstName || "").trim();
  const cleanUsername = String(username || "").trim();

  return cleanFirstName || cleanUsername || "Seller";
};

const formatNotificationDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const SellerTopbar = ({
  collapsed,
  onToggleSidebar,
  notifications = [],
  unreadNotificationsCount = 0,
  onMarkNotificationAsRead,
  onClearNotifications,
}: SellerTopbarProps) => {
  const location = useLocation();
  const { token, userId, firstName, username, logout } = useAuth();

  const [resolvedFirstName, setResolvedFirstName] = useState<string>(
    buildFirstName(firstName || "", username),
  );
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchCurrentUser = async () => {
      if (!token || !userId) {
        setResolvedFirstName(buildFirstName(firstName || "", username));
        return;
      }

      try {
        const response = await authFetch(`/v2/users/${userId}`, { token });
        const payload = (await response.json()) as CurrentUserResponse;

        if (!response.ok || !payload?.user) {
          throw new Error(payload?.error || "No se pudo obtener el usuario actual.");
        }

        const name = buildFirstName(
          payload.user.first_name || "",
          payload.user.username || username,
        );

        if (!cancelled) {
          setResolvedFirstName(name);
        }
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setResolvedFirstName(buildFirstName(firstName || "", username));
        }
      }
    };

    fetchCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [token, userId, firstName, username]);

  const pageSubtitle = useMemo(() => getPageSubtitle(location.pathname), [location.pathname]);

  const visibleNotifications = useMemo(
    () => notifications.slice(0, 50),
    [notifications],
  );

  const handleToggleNotifications = () => {
    setNotificationsOpen((prev) => !prev);
  };

  const handleMarkSingleAsRead = (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string,
  ) => {
    event.stopPropagation();
    onMarkNotificationAsRead?.(notificationId);
  };

  return (
    <div style={wrapperStyle}>
      <div style={barStyle}>
        <div style={leftStyle}>
          <div title={collapsed ? "Expandir menú" : "Contraer menú"}>
            <Button
              variant="secondary"
              onClick={onToggleSidebar}
              style={compactButtonStyle}
            >
              {collapsed ? "→" : "←"}
            </Button>
          </div>

          <div style={titleWrapStyle}>
            <span style={titleStyle}>{`Hola, ${resolvedFirstName}`}</span>
            <span style={subtitleStyle}>{pageSubtitle}</span>
          </div>
        </div>

        <div style={rightStyle}>
          <FeedbackButton buttonStyle={bellButtonStyle} />

          <div style={bellWrapStyle}>
            <button
              type="button"
              style={bellButtonStyle}
              onClick={handleToggleNotifications}
              title="Notificaciones"
              aria-label="Notificaciones"
            >
              🔔

              {unreadNotificationsCount > 0 ? (
                <span style={badgeStyle}>
                  {unreadNotificationsCount > 9 ? "9+" : unreadNotificationsCount}
                </span>
              ) : null}
            </button>

            {notificationsOpen ? (
              <div style={panelStyle}>
                <div style={panelHeaderStyle}>
                  <h3 style={panelTitleStyle}>Notificaciones</h3>

                  {visibleNotifications.length ? (
                    <button
                      type="button"
                      style={panelActionStyle}
                      onClick={onClearNotifications}
                    >
                      Marcar todas
                    </button>
                  ) : null}
                </div>

                <div style={listStyle}>
                  {visibleNotifications.length ? (
                    visibleNotifications.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          ...itemStyle,
                          ...(!item.read ? itemUnreadStyle : {}),
                        }}
                      >
                        <div style={itemTitleStyle}>{item.title}</div>
                        <div style={itemBodyStyle}>{item.body}</div>
                        <div style={itemDateStyle}>
                          {formatNotificationDate(item.receivedAt)}
                        </div>

                        {!item.read ? (
                          <button
                            type="button"
                            style={itemActionStyle}
                            onClick={(event) =>
                              handleMarkSingleAsRead(event, item.id)
                            }
                          >
                            Marcar leída
                          </button>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div style={emptyStyle}>Todavía no hay notificaciones.</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <ThemeToggle />

          <button
            type="button"
            style={profileChipStyle}
            title="Perfil"
            aria-label="Perfil"
          >
            ⚙
          </button>

          <Button variant="ghost" onClick={logout} style={compactButtonStyle}>
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SellerTopbar;